import { Stack } from "@mui/material";
import { Button } from "@src/appV2/redesign/components/Button";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { IconButton } from "@src/appV2/redesign/components/IconButton";
import { Illustration, Text } from "@src/appV2/redesign/system/ui-components";
import { chunk } from "lodash";
import { useMemo } from "react";

import { PinToEnter, type PinToEnterProps } from "./PinToEnter";

export interface UseInternalTimeClockContentProps {
  action: "clock-in" | "clock-out";
  timeclockLocation: string;
  pinsToEnter: string;
  badgeNumber: string;
  onActionConfirm: () => void;
  onHelp: () => void;
  onCancel: () => void;
}

function getActionTexts(
  action: "clock-in" | "clock-out",
  timeclockLocation: string
): { timeclockLocationParagraph: string; useTheAppParagraph: string; actionConfirmButton: string } {
  switch (action) {
    case "clock-in": {
      return {
        timeclockLocationParagraph: `This facility requires you to clock in through their internal time clock which is located ${timeclockLocation}.`,
        useTheAppParagraph:
          "At the end of your shift, you will need to clock out and get your timesheet signed using the Clipboard app.",
        actionConfirmButton: "I have clocked in",
      };
    }

    case "clock-out": {
      return {
        timeclockLocationParagraph: `This facility requires you to clock out through their internal time clock which is located ${timeclockLocation}.`,
        useTheAppParagraph: "",
        actionConfirmButton: "I have clocked out",
      };
    }

    default: {
      return action satisfies never;
    }
  }
}

export function UseInternalTimeClockContent(props: UseInternalTimeClockContentProps) {
  const { action, timeclockLocation, pinsToEnter, badgeNumber, onActionConfirm, onHelp, onCancel } =
    props;
  const texts = getActionTexts(action, timeclockLocation);
  const pins = useMemo<PinToEnterProps[]>(() => {
    const parts = chunk(
      pinsToEnter
        /** Expected format: "Agency ID: *210*Your badge number:*{badgeNumber}*"
         * -> will be turned into two pins:
         *  `[{ name: "Agency ID", pin: "210" }, { name: "Your badge number", pin: <badge number> }]`
         *  `name` will be rendered with smaller font size, `pin` with larger, on a new line
         *  it's possible for `name` to be an empty string, e.g. if there are multiple *texts* in a row, or the input
         *  starts with a *text*
         */
        .split("*")
        .map((s) => s.trim()),
      2
    );

    return parts.map(([name = "", pin = ""]) => ({
      name,
      pin: pin.replace("{badgeNumber}", badgeNumber),
    }));
  }, [pinsToEnter, badgeNumber]);

  return (
    <Stack sx={{ overflowY: "hidden" }}>
      <Stack
        sx={(theme) => ({
          paddingTop: theme.spacing(5),
          paddingX: theme.spacing(4),
          justifyContent: "center",
          alignItems: "flex-end",
        })}
      >
        <IconButton aria-label="Close" variant="outlined" iconType="close" onClick={onCancel} />
      </Stack>
      <Stack sx={{ overflowY: "scroll", paddingBottom: 4 }}>
        <Stack sx={{ alignItems: "center" }}>
          <Illustration type="clock-in-pending" />
        </Stack>
        <Stack
          sx={(theme) => ({
            alignItems: "center",
            paddingTop: theme.spacing(4),
            paddingX: theme.spacing(4),
            gap: theme.spacing(7),
          })}
        >
          <Text variant="h2" textAlign="center">
            Use the internal time clock
          </Text>
          <Text variant="body1" textAlign="center">
            {texts.timeclockLocationParagraph}
          </Text>
          {texts.useTheAppParagraph && (
            <Text
              variant="body1"
              textAlign="center"
              color={(theme) => theme.palette.intent?.error.text}
            >
              {texts.useTheAppParagraph}
            </Text>
          )}
          <Stack sx={(theme) => ({ gap: theme.spacing(4) })}>
            {pins.map((pinToEnterProps) => (
              <PinToEnter key={pinToEnterProps.name} {...pinToEnterProps} />
            ))}
          </Stack>
        </Stack>
      </Stack>
      <DialogFooter withShadow orientation="vertical">
        <Button fullWidth size="large" onClick={onActionConfirm}>
          {texts.actionConfirmButton}
        </Button>
        <Button fullWidth size="large" variant="outlined" onClick={onHelp}>
          I need help
        </Button>
      </DialogFooter>
    </Stack>
  );
}
